import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Input, Icon } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { save } from "../../api/Category"

/* Widgets */
import { toast } from "../common"

/* Shared */
import { SIZES, pictureOf, plural, toneOf } from "./shared"

Modal.setAppElement("#root")


/* The empty form of a new category */
const INITIAL = {

    id: null,
    name: "",
    typeID: 0,
    size: "small",
    organizations: 0,

    /* The picture already saved, the file just chosen and its preview — for the app and for the site */
    image: null,
    imageFile: null,
    imagePreview: null,
    removeImage: false,

    webImage: null,
    webImageFile: null,
    webImagePreview: null,
    removeWebImage: false
}


/* Widget Category Modal — a new category and the card of an old one are the same form.
   It is opened by the reference: this._category.current.open(item, typeID) */
class CategoryModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            waiting: false,
            ...INITIAL
        }
    }

    /* Open the form — with a category it is its card, without one a new category in the given group */
    open = (item = null, typeID = 0) => {

        if (item) {
            this.setState({
                ...INITIAL,
                visible: true,
                waiting: false,
                id: item.id,
                name: item.name ? item.name : "",
                typeID: parseInt(item.typeID) || 0,
                size: item.size === "big" ? "big" : "small",
                organizations: parseInt(item.organizations) || 0,
                image: item.image,
                webImage: item.webImage
            })
            return
        }

        this.setState({ ...INITIAL, visible: true, waiting: false, typeID: parseInt(typeID) || 0 })
    }

    /* Close the form */
    close = () => {

        /* The request is already on its way */
        if (this.state.waiting) {
            return
        }

        this.setState({ visible: false })
    }

    /* Choose a picture — "image" for the app, "webImage" for the site */
    choose = (field, event) => {

        const file = event.target.files[0]

        /* The same file can be chosen again after it was taken away */
        event.target.value = ""

        if (!file) {
            return
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error("Картинка больше 10 МБ")
            return
        }

        this.setState({ [`${field}File`]: file, [`${field}Preview`]: URL.createObjectURL(file), [`remove${field === "image" ? "Image" : "WebImage"}`]: false })
    }

    /* Take a picture away — a fresh one is just forgotten, a saved one is removed on saving */
    clear = field => {

        const flag = field === "image" ? "removeImage" : "removeWebImage"

        if (this.state[`${field}File`]) {
            this.setState({ [`${field}File`]: null, [`${field}Preview`]: null })
            return
        }

        this.setState({ [flag]: true })
    }

    /* The picture which the form shows now */
    previewOf = field => {

        const flag = field === "image" ? "removeImage" : "removeWebImage"

        if (this.state[`${field}Preview`]) {
            return this.state[`${field}Preview`]
        }

        if (this.state[flag]) {
            return null
        }

        return pictureOf(this.state[field])
    }

    /* Save the category */
    save = () => {

        /* Fields */
        const { id, name, typeID, size, imageFile, webImageFile, removeImage, removeWebImage, waiting } = this.state
        const { onSaved } = this.props

        /* The request is already on its way */
        if (waiting) {
            return
        }

        if (name.trim().length === 0) {
            this._name.error("Введите название категории")
            return
        }

        if (!typeID) {
            toast.error("Выберите группу")
            return
        }

        const body = new FormData()

        if (id) {
            body.append("id", id)
        }

        body.append("name", name.trim())
        body.append("typeID", typeID)
        body.append("size", size)

        if (imageFile) {
            body.append("image", imageFile)
        }
        else if (removeImage) {
            body.append("removeImage", 1)
        }

        if (webImageFile) {
            body.append("webImage", webImageFile)
        }
        else if (removeWebImage) {
            body.append("removeWebImage", 1)
        }

        this.setState({ waiting: true })

        save(body).then(response => {

            if (response && response.status === 200) {
                this.setState({ waiting: false, visible: false })
                onSaved()
                toast.success(id ? "Категория сохранена" : "Категория создана")
                return
            }

            this.setState({ waiting: false })

            if (response && response.data && response.data.code === "NOT_AN_IMAGE") {
                toast.error("Можно загрузить только картинку")
                return
            }

            if (response && response.data && response.data.code === "GROUP_NOT_FOUND") {
                toast.error("Группа не найдена — обновите страницу")
                return
            }

            toast.error("Что-то пошло не так! Попробуйте позднее")
        }).catch(() => {
            this.setState({ waiting: false })
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    /* One slot of a picture */
    _picture = (field, title, hint, accept) => {

        const preview = this.previewOf(field)

        if (preview) {
            return (
                <div className="category-modal-picture filled">
                    <div className="category-modal-picture-frame">
                        <img src={preview} alt={title} />
                    </div>
                    <div className="category-modal-picture-text">
                        <b>{title}</b>
                        <span>{hint}</span>
                        <div className="category-modal-picture-actions">
                            <label className="category-modal-picture-link">
                                <input onChange={event => this.choose(field, event)} type="file" accept={accept} />
                                Заменить
                            </label>
                            <div onClick={() => this.clear(field)} className="category-modal-picture-link danger">Убрать</div>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <label className="category-modal-picture empty">
                <input onChange={event => this.choose(field, event)} type="file" accept={accept} />
                <div className="category-modal-picture-frame">
                    <Icon name="image" size={22} />
                </div>
                <div className="category-modal-picture-text">
                    <b>{title}</b>
                    <span>{hint}</span>
                    <div className="category-modal-picture-actions">
                        <div className="category-modal-picture-link">Загрузить</div>
                    </div>
                </div>
            </label>
        )
    }

    /* The tile as the app will draw it */
    _preview = () => {

        /* Fields */
        const { name, size, typeID } = this.state
        const { groups } = this.props

        const group = groups.find(item => parseInt(item.id) === parseInt(typeID))
        const tone = toneOf(typeID)
        const image = this.previewOf("image")

        return (
            <div className="category-modal-preview" style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}>

                <div className="category-modal-preview-label">Так будет на главной</div>

                <div className="category-modal-preview-group">{group ? group.name : "Группа не выбрана"}</div>

                <div className="category-modal-preview-row">

                    <div className={`category-modal-preview-tile ${size}`}>
                        {image
                            ? <img src={image} alt="Preview" />
                            : <div className="category-modal-preview-empty"><Icon name="category" size={22} /></div>
                        }
                        <span>{name.trim().length > 0 ? name.trim() : "Название"}</span>
                    </div>

                    {size === "small" && <div className="category-modal-preview-tile ghost" />}
                    <div className="category-modal-preview-tile ghost" />

                </div>

            </div>
        )
    }

    /* The body of the form */
    _body = () => {

        /* Fields */
        const { id, name, typeID, size, organizations, waiting } = this.state
        const { groups } = this.props

        return (
            <div className="wide-modal-body">

                {this._preview()}

                <div className="wide-modal-label">Название</div>

                <Input ref={ref => this._name = ref} label="Название категории" maxLength={100} value={name} onChange={event => this.setState({ name: event.target.value })} />

                <div className="wide-modal-label">Группа</div>
                <div className="wide-modal-hint">Раздел главной страницы приложения, в котором стоит категория.</div>

                <div className="category-modal-groups">
                    {groups.map(group => {

                        const tone = toneOf(group.id)
                        const active = parseInt(typeID) === parseInt(group.id)

                        return (
                            <div
                                key={`${group.id}`}
                                onClick={() => this.setState({ typeID: parseInt(group.id) })}
                                style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}
                                className={`category-modal-group ${active ? "active" : ""} ${parseInt(group.visible) === 1 ? "" : "hidden"}`}
                            >
                                <i />
                                {group.name}
                                {parseInt(group.visible) !== 1 && <Icon name="eyeOff" size={13} width={2} />}
                            </div>
                        )
                    })}
                </div>

                <div className="wide-modal-label">Плитка</div>

                <div className="category-modal-sizes">
                    {SIZES.map(item =>
                        <div key={item.key} onClick={() => this.setState({ size: item.key })} className={`category-modal-size ${size === item.key ? "active" : ""}`}>
                            <div className={`category-modal-size-shape ${item.key}`}>
                                <i />{item.key === "small" && <i />}<i />
                            </div>
                            <div className="category-modal-size-text">
                                <b>{item.text}</b>
                                <span>{item.hint}</span>
                            </div>
                            <div className="category-modal-size-check">
                                <Icon name="check" size={12} width={3} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="wide-modal-label">Картинки</div>

                <div className="category-modal-pictures">
                    {this._picture("image", "Приложение", "PNG или JPG, квадрат", "image/png, image/jpeg")}
                    {this._picture("webImage", "Сайт", "SVG или PNG — иконка", "image/svg+xml, image/png, image/jpeg")}
                </div>

                {id && organizations > 0 &&
                    <div className="category-modal-note">
                        <Icon name="organization" size={16} />
                        <span>Категорию выбрали <b>{organizations} {plural(organizations, "точка", "точки", "точек")}</b> — название поменяется и у них.</span>
                    </div>
                }

                <div onClick={() => this.save()} className={`organization-details-button sticky ${waiting ? "disabled" : ""}`}>
                    {waiting ? <LoadingOutlined /> : id ? "Сохранить" : "Создать категорию"}
                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, id } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>{id ? "Категория" : "Новая категория"}</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {this._body()}

            </Modal>
        )
    }
}

export default CategoryModal
